import { useState, useRef, useEffect, useCallback } from "react";
import {
  getNotebooksByUser,
  getNotebookById,
  updateNotebook,
  createNotebook,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Button } from "../../client/components/ui/button";
import { Plus, FileText } from "lucide-react";
import NotebookEditor from "../../notebook/LazyNotebookEditor";
import { useWidgetData } from "./WidgetDataContext";
import TemplateManager from "../../trading-note-templates/TemplateManager";
import { markdownToHtml } from "../../notebook/markdownToHtml";
import { htmlToMarkdown } from "../../notebook/htmlToMarkdown";
import type { TradingNoteTemplate } from "wasp/entities";

import type { WidgetViewProps } from "./types";

interface NotebookBrowserWidgetProps extends WidgetViewProps {
  initialNoteId?: string;
}

export default function NotebookBrowserWidget({ initialNoteId, viewMode, onSetViewMode }: NotebookBrowserWidgetProps = {}) {
  const { isDark, t, notebookInitialNoteId } = useWidgetData();
  const { data: notes } = useQuery(getNotebooksByUser);
  const create = useAction(createNotebook);
  const update = useAction(updateNotebook);

  const effectiveInitial = initialNoteId ?? notebookInitialNoteId;
  const [selectedId, setSelectedId] = useState<string | null>(effectiveInitial ?? null);

  useEffect(() => {
    if (selectedId) return;
    if (effectiveInitial) { setSelectedId(effectiveInitial); return; }
    if (notes && notes.length > 0) setSelectedId(notes[0].id);
  }, [notes, effectiveInitial, selectedId]);

  const { data: note } = useQuery(
    getNotebookById,
    { id: selectedId ?? "" },
    { enabled: !!selectedId },
  );

  const [content, setContent] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const pendingHtmlRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedIdRef = useRef<string | null>(null);

  // The template picker is not a persisted property of a note — reset it when
  // the selected note changes.
  useEffect(() => { setSelectedTemplateId(undefined); }, [selectedId]);

  useEffect(() => {
    if (!note) return;
    // Load content when the note changes (loadedIdRef !== note.id), OR when a
    // background refetch arrives and the user has no unsaved edits
    // (pendingHtmlRef === null). When the same note is showing AND an edit is
    // pending, skip — don't clobber the user's in-progress text.
    if (loadedIdRef.current !== note.id || pendingHtmlRef.current === null) {
      setContent(note.content ?? "");
      loadedIdRef.current = note.id;
    }
  }, [note?.id, note?.content]);

  const flushSave = useCallback(() => {
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
    if (pendingHtmlRef.current !== null && selectedId) {
      update({ id: selectedId, content: pendingHtmlRef.current });
      pendingHtmlRef.current = null;
    }
  }, [selectedId, update]);

  const flushSaveRef = useRef(flushSave);
  useEffect(() => { flushSaveRef.current = flushSave; }, [flushSave]);
  useEffect(() => () => { flushSaveRef.current(); }, []);

  const handleChange = useCallback((html: string) => {
    if (!selectedId) return;
    pendingHtmlRef.current = html;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      update({ id: selectedId, content: html });
      pendingHtmlRef.current = null;
    }, 800);
  }, [selectedId, update]);

  // Apply a note template to the current note (mirrors TradeDetailPage):
  // template content on top; if the note already has content, append it below
  // a divider. Templates are Markdown; the note editor is HTML — convert both ways.
  const handleApplyTemplate = useCallback((template: TradingNoteTemplate) => {
    if (!template.id) {
      // "Aucun template" / clear — no content change
      setSelectedTemplateId(undefined);
      return;
    }
    if (!selectedId) return;
    // Prefer the most recently typed HTML (held in pendingHtmlRef during the
    // 800ms debounce window) over the `content` state, which only updates after
    // a server-loaded sync — otherwise text typed just before opening the picker
    // would be dropped from the merge.
    const currentHtml = pendingHtmlRef.current ?? content;
    const existingMd = currentHtml && currentHtml.trim() !== "" ? htmlToMarkdown(currentHtml).trim() : "";
    // Compare against the round-tripped template so conversion-normalisation noise
    // (htmlToMarkdown(markdownToHtml(x)) !== x) doesn't make a note that already
    // holds exactly this template look "different" and get the template appended twice.
    const templateRoundTripped = htmlToMarkdown(markdownToHtml(template.content)).trim();
    let newMd = template.content;
    if (existingMd && existingMd !== templateRoundTripped) {
      newMd = `${template.content}\n\n---\n\n**${t.tradeDetail.previousContent}:**\n\n${existingMd}`;
    }
    const html = markdownToHtml(newMd);
    setContent(html);    // update editor display immediately (controlled value)
    handleChange(html);  // arm the debounced persist (sets pendingHtmlRef)
    setSelectedTemplateId(template.id);
  }, [selectedId, content, t, handleChange]);

  const selectNote = useCallback((id: string) => {
    flushSave();
    setSelectedId(id);
  }, [flushSave]);

  const handleNewNote = useCallback(async () => {
    flushSave();
    const created = await create({ title: t.notebook.untitled, content: "", symbols: [], tags: [], images: [] });
    if (created?.id) setSelectedId(created.id);
  }, [create, flushSave, t]);

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardSectionHeader
        label={t.dashboard.widgets.notebookBrowser}
        isDark={isDark}
        compact
        rightContent={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {selectedId && note && (
              <TemplateManager
                onSelectTemplate={handleApplyTemplate}
                selectedTemplateId={selectedTemplateId}
                compact
                triggerClassName="w-[150px] h-7 text-xs"
              />
            )}
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleNewNote}>
              <Plus className="h-3 w-3" />
              {t.notebook.newNote}
            </Button>
          </div>
        }
      />
      <CardContent className="flex-1 flex overflow-hidden p-0">
        <div className="w-1/4 min-w-[180px] border-r border-border overflow-y-auto">
          {(notes ?? []).length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">{t.notebook.empty}</div>
          ) : (
            (notes ?? []).map((n: any) => (
              <button
                key={n.id}
                onClick={() => selectNote(n.id)}
                className={`w-full text-left px-3 py-2 border-b border-border/50 hover:bg-accent/40 transition-colors ${n.id === selectedId ? "bg-accent/60" : ""}`}
              >
                <p className="text-xs font-medium truncate text-foreground">{n.title || t.notebook.untitled}</p>
                <p className="text-[10px] text-muted-foreground">{new Date(n.updatedAt).toLocaleDateString()}</p>
              </button>
            ))
          )}
        </div>
        <div className="flex-1 overflow-hidden flex flex-col">
          {selectedId && note ? (
            <NotebookEditor
              key={selectedId ?? "empty"}
              content={content}
              onChange={handleChange}
              className="h-full flex flex-col"
              contentClassName="p-4 flex-1 overflow-auto cursor-text"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <FileText className="h-6 w-6 opacity-40" />
              <p className="text-xs">{t.notebook.selectNote}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
